import React, { useEffect, useState, useRef, useCallback } from "react";
import toast, { Toaster } from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";
import notificationSound from "../sounds/notification.mp3";

const SOCKET_URL = "wss://renukahydraulic.imrtechsolutions.com/ws/notifications/";

const getIconAndColor = (type) => {
  switch (type) {
    case "LOW_STOCK_ALERT":
      return { icon: "🛒", color: "#ff9800" };
    case "EXPIRY_ALERT":
      return { icon: "⏳", color: "#f44336" };
    case "BACKORDER_RESOLVED":
      return { icon: "✅", color: "#4caf50" };
    case "NEW_FRANCHISE_ENQUIRY":
      return { icon: "🏪", color: "#007bff" };
    case "NEW_SERVICE_ENQUIRY":
      return { icon: "💼", color: "#28a745" };
    case "ORDER_PLACED":
      return { icon: "🧾", color: "#673ab7" };
    default:
      return { icon: "🔔", color: "#6c757d" };
  }
};

const formatNotification = (notif) => {
  switch (notif.type) {
    case "LOW_STOCK_ALERT":
      return `🛒 Low Stock: ${notif.product} (Stock: ${notif.total_stock}, Threshold: ${notif.threshold})`;
    case "EXPIRY_ALERT":
      return `⏳ Expiry Alert: ${notif.product} (Batch ${notif.batch_id}) expires on ${notif.expiry_date}`;
    case "BACKORDER_RESOLVED":
      return `✅ Backorder Resolved: ${notif.product} (${notif.quantity} units by ${notif.user})`;
    case "NEW_FRANCHISE_ENQUIRY":
      return `🏪 Franchise Enquiry from ${notif.full_name}`;
    case "NEW_SERVICE_ENQUIRY":
      return `💼 Service Enquiry from ${notif.full_name} (${notif.enquiry_type})`;
    case "ORDER_PLACED":
      return `🧾 New Order #${notif.order_id} - ₹${notif.total_amount}`;
    default:
      return `🔔 ${notif.type}: ${notif.product || notif.message}`;
  }
};

const formatTimeAgo = (timestamp) => {
  if (!timestamp) return "Just now";
  const now = new Date();
  const time = new Date(timestamp);
  const diff = Math.floor((now - time) / 1000);
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const Notification = () => {
  const [notifications, setNotifications] = useState(() => {
    const saved = localStorage.getItem("all_notifications");
    return saved ? JSON.parse(saved) : [];
  });

  const [unreadCount, setUnreadCount] = useState(() => {
    const saved = localStorage.getItem("all_unread_count");
    return saved ? parseInt(saved, 10) : 0;
  });

  const socketRef = useRef(null);
  const audioRef = useRef(null);

  const initializeSocket = useCallback(() => {
    socketRef.current = new WebSocket(SOCKET_URL);

    socketRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // console.log("🔔 Notification received:", data);

        const message = formatNotification(data);

        toast.success(message, { duration: 6000 });
        audioRef.current?.play().catch((err) => console.warn("🔇 Audio blocked:", err));

        const newNotif = {
          id: uuidv4(),
          ...data,
          message,
          timestamp: new Date().toISOString(),
        };

        const updated = [newNotif, ...notifications].slice(0, 30);
        setNotifications(updated);
        localStorage.setItem("all_notifications", JSON.stringify(updated));

        const next = unreadCount + 1;
        setUnreadCount(next);
        localStorage.setItem("all_unread_count", next.toString());

      } catch (err) {
        console.error("❌ Failed to process notification:", err);
      }
    };

    socketRef.current.onclose = () => {
      console.warn("🔌 WebSocket disconnected. Retrying...");
      setTimeout(() => initializeSocket(), 5000);
    };

    socketRef.current.onerror = (error) => {
      console.error("WebSocket error:", error);
      socketRef.current.close();
    };
  }, [notifications, unreadCount]);

  useEffect(() => {
    initializeSocket();
    return () => socketRef.current?.close();
  }, [initializeSocket]);

  useEffect(() => {
    const pingInterval = setInterval(() => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({ ping: true }));
      }
    }, 30000);
    return () => clearInterval(pingInterval);
  }, []);

  return (
    <>
      <Toaster position="top-right" />
      <audio ref={audioRef} src={notificationSound} preload="auto" />

      <div className="notification-list">
        {notifications.length === 0 ? (
          <div className="text-center text-muted py-4">No notifications yet</div>
        ) : (
          notifications.map((notif) => {
            const { icon } = getIconAndColor(notif.type);
            return (
              <div
                key={notif.id}
                className="notification-item border-bottom"
                style={{
                  padding: "12px 16px"
                }}
              >
                <div className="d-flex align-items-start">
                  <div className="me-3" style={{ fontSize: "1.5rem" }}>{icon}</div>
                  <div className="flex-grow-1">
                    <strong>{notif.message}</strong>
                    <div className="text-muted mt-1">
                      {notif.email && <div>Email: {notif.email}</div>}
                      {notif.phone && <div>Phone: {notif.phone}</div>}
                      {notif.product && <div>Product: {notif.product}</div>}
                    </div>
                    <small className="text-muted mt-2 d-block">
                      {formatTimeAgo(notif.timestamp)}
                    </small>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

    </>
  );
};

export default Notification;
